/**
 * Show Actions Section Component
 *
 * Displays missing/invalid show configuration details as a warning banner.
 * Only shows items that need attention - banner disappears when all checks pass.
 *
 * @module ShowActionsSection
 */
import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { AlertTriangle } from 'lucide-react';

interface ShowActionsSectionProps {
  customPublishWeekdays?: string | null;
  slug?: string | null;
  mixingSpec?: string | null;
  // Only timestamp fields needed (filenames removed from DB)
  imageUpdatedAt?: string | null;
  introMusicUpdatedAt?: string | null;
  outroMusicUpdatedAt?: string | null;
}

interface ValidationIssue {
  id: string;
  message: string;
}

/**
 * Validates slug format: lowercase alphanumeric + hyphens, no leading/trailing hyphens
 */
const validateSlugFormat = (slug: string | null | undefined): boolean => {
  if (!slug || typeof slug !== 'string') return false;
  const slugRegex = /^[a-z0-9-]+$/;
  return slugRegex.test(slug) && !slug.startsWith('-') && !slug.endsWith('-');
};

export const ShowActionsSection: React.FC<ShowActionsSectionProps> = ({
  customPublishWeekdays,
  slug,
  mixingSpec,
  imageUpdatedAt,
  introMusicUpdatedAt,
  outroMusicUpdatedAt,
}) => {
  // Compute validation issues - only failed checks are tracked
  const issues = useMemo<ValidationIssue[]>(() => {
    const problems: ValidationIssue[] = [];

    // Check 1: Show image
    if (!imageUpdatedAt) {
      problems.push({
        id: 'image',
        message: 'Show image missing or not uploaded',
      });
    }

    // Check 2: Intro music
    if (!introMusicUpdatedAt) {
      problems.push({
        id: 'intro-music',
        message: 'Intro music missing or not uploaded',
      });
    }

    // Check 3: Outro music
    if (!outroMusicUpdatedAt) {
      problems.push({
        id: 'outro-music',
        message: 'Outro music missing or not uploaded',
      });
    }

    // Check 4: Schedule (custom weekdays set)
    if (!customPublishWeekdays?.trim()) {
      problems.push({
        id: 'schedule',
        message: 'Publishing schedule not configured',
      });
    }

    // Check 5: Mixing spec configured
    if (!mixingSpec?.trim()) {
      problems.push({
        id: 'mixing-spec',
        message: 'Audio mixing specification not configured',
      });
    }

    // Check 6: Slug validation
    if (!validateSlugFormat(slug)) {
      problems.push({
        id: 'slug',
        message:
          'Invalid slug format (use lowercase letters, numbers, and hyphens only)',
      });
    }

    return problems;
  }, [
    customPublishWeekdays,
    slug,
    mixingSpec,
    imageUpdatedAt,
    introMusicUpdatedAt,
    outroMusicUpdatedAt,
  ]);

  // If no issues, don't render the banner
  if (issues.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        p: 1.5,
        bgcolor: 'background.paper',
        borderRadius: 1,
        border: 1,
        borderColor: 'divider',
        borderTop: '5px solid #ed6c02', // Warning color (orange)
        mb: 2,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}
      >
        Missing Details
      </Typography>

      {/* Issues list - compact inline format */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {issues.map(issue => (
          <Box
            key={issue.id}
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <AlertTriangle size={14} color="#ed6c02" />
            <Typography variant="body2">{issue.message}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
